import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { formatVendorStartingPrice } from '../utils/pricing';
import { getVendorAvatar } from '../utils/vendorImage';
import { useAppLocation } from '../contexts/LocationContext';
import { formatLocationHeader } from '../utils/locationFormat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TimePicker from '../components/ui/TimePicker';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { 
  Calendar, MapPin, Users, DollarSign, ArrowRight, ArrowLeft, 
  Check, Sparkles, PartyPopper, Music, Camera, Utensils, Building2,
  Palette, X, Plus, Minus, Send, CheckCircle2, Home, LocateFixed, Search, Star, ExternalLink, Clock, Loader2, Shield, Zap, AlertCircle, Locate, LayoutList,
  Cake, Flower2, Scissors, ClipboardList, Signpost, Crown
} from 'lucide-react';
import { CeleyPageLoader } from '../components/CeleyLoader';
import AuthGateModal from '../components/AuthGateModal';
import { isFixedLocationProvider, getServiceAreaDisplay } from '../utils/vendorDisplay';
import { BadgeList } from '../components/BadgeDisplay';
import CeleyDelightLayer from '../components/CeleyDelightLayer';
import { getEventTheme, MOOD_CONFIG, getEventCopy } from '../services/eventThemeService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// Primary event type tiles (most popular — displayed as big tiles)
// Shared with CustomerLandingPage; canonical 6-tile registry lives in
// /app/frontend/src/constants/primaryEventTypes.js
import { PRIMARY_EVENT_TYPES, EXPANDED_EVENT_TYPES } from '../constants/primaryEventTypes';
import { getTemplateForEventType } from '../services/eventTemplates';

// Popular service tiles (displayed as big tiles by default)
const POPULAR_SERVICES = [
  { value: 'DJ', label: 'DJ', icon: Music },
  { value: 'Photographer', label: 'Photographer', icon: Camera },
  { value: 'Videographer', label: 'Videographer', icon: Camera },
  { value: 'Caterer', label: 'Catering', icon: Utensils },
  { value: 'Venue', label: 'Venue', icon: Building2 },
  { value: 'Balloon Artist', label: 'Balloons', icon: PartyPopper },
  { value: 'Event Decor & Styling', label: 'Decor', icon: Palette },
  { value: 'Photo Booth Provider', label: 'Photo Booth', icon: Camera },
  { value: 'Bounce House / Inflatable Rentals', label: 'Bounce House', icon: PartyPopper },
  { value: 'Face Painters', label: 'Face Painting', icon: Palette },
  // Common vendor categories that exist in the backend but were missing
  // tiles — added Feb 2026 so seeded categories visibly highlight on Step 2.
  // Each value MUST match the backend category string exactly.
  { value: 'Bakery', label: 'Bakery', icon: Cake },
  { value: 'Cake Bakers', label: 'Cake Bakers', icon: Cake },
  { value: 'Florist', label: 'Florist', icon: Flower2 },
  { value: 'Hair & Makeup', label: 'Hair & Makeup', icon: Scissors },
  { value: 'Event Planner', label: 'Event Planner', icon: ClipboardList },
  { value: 'Yard Sign Rentals', label: 'Yard Signs', icon: Signpost },
  { value: 'Throne Chair Rentals', label: 'Throne Chairs', icon: Crown },
];

// Format currency - handles numeric values
const formatCurrency = (amount) => {
  if (!amount || amount <= 0) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Category → icon mapping for fallback avatars
const CATEGORY_ICON_MAP = {
  'Caterer': { icon: Utensils, bg: 'bg-orange-50', color: 'text-orange-500' },
  'Catering': { icon: Utensils, bg: 'bg-orange-50', color: 'text-orange-500' },
  'DJ': { icon: Music, bg: 'bg-purple-50', color: 'text-purple-500' },
  'Photographer': { icon: Camera, bg: 'bg-blue-50', color: 'text-blue-500' },
  'Videographer': { icon: Camera, bg: 'bg-blue-50', color: 'text-blue-500' },
  'Venue': { icon: Building2, bg: 'bg-slate-50', color: 'text-slate-600' },
  'Balloon Artist': { icon: PartyPopper, bg: 'bg-pink-50', color: 'text-pink-500' },
  'Event Decor & Styling': { icon: Palette, bg: 'bg-rose-50', color: 'text-rose-500' },
  'Photo Booth Provider': { icon: Camera, bg: 'bg-indigo-50', color: 'text-indigo-500' },
  'Bounce House / Inflatable Rentals': { icon: PartyPopper, bg: 'bg-amber-50', color: 'text-amber-500' },
  'Face Painters': { icon: Palette, bg: 'bg-teal-50', color: 'text-teal-500' },
};

const getCategoryIcon = (vendor) => {
  const cats = vendor.categories || [];
  const matched = vendor.matched_category || cats[0] || '';
  for (const [key, val] of Object.entries(CATEGORY_ICON_MAP)) {
    if (matched.toLowerCase().includes(key.toLowerCase())) return val;
  }
  for (const cat of cats) {
    for (const [key, val] of Object.entries(CATEGORY_ICON_MAP)) {
      if (cat.toLowerCase().includes(key.toLowerCase())) return val;
    }
  }
  return { icon: Sparkles, bg: 'bg-emerald-50', color: 'text-emerald-500' };
};

// Format preview price — delegates to the canonical pricing helper so the
// preview matches BrowseVendors + VendorDetail exactly (Phase 5 alignment).
// Returns null ONLY when we truly have nothing to show (no services + no
// bucket) so the existing "— price hidden" fallback in the card layer works.
const formatPreviewPrice = (vendor) => {
  const display = formatVendorStartingPrice(vendor, (bucket) => {
    if (typeof vendor.base_price === 'number' && vendor.base_price > 0) {
      return `Starting at ${formatCurrency(vendor.base_price)}`;
    }
    // Mirror the previous inline parser for bucket-only vendors.
    const raw = Array.isArray(bucket) ? bucket[0] : bucket;
    if (typeof raw === 'string' && raw.length > 0) {
      const rangeMatch = raw.match(/\$?([\d,]+)\s*[-–]\s*\$?([\d,]+)/);
      if (rangeMatch) return `$${rangeMatch[1]}–$${rangeMatch[2]}`;
      const singleMatch = raw.match(/\$?([\d,]+)/);
      if (singleMatch) return `Starting at $${singleMatch[1]}`;
      return raw;
    }
    return null;
  });
  return display === 'Request pricing' ? null : display;
};

// Generate fit insight from vendor categories + event context
const getFitInsight = (vendor) => {
  const cats = (vendor.categories || []).map(c => c.toLowerCase());
  const matched = (vendor.matched_category || '').toLowerCase();
  const all = [matched, ...cats].filter(Boolean);
  if (all.some(c => c.includes('cater'))) return 'Great for large events';
  if (all.some(c => c.includes('dj'))) return 'Perfect for parties';
  if (all.some(c => c.includes('photograph'))) return 'Captures your best moments';
  if (all.some(c => c.includes('videograph'))) return 'Great for event videography';
  if (all.some(c => c.includes('balloon'))) return 'Best for themed setups';
  if (all.some(c => c.includes('decor') || c.includes('styling'))) return 'Great for themed decorations';
  if (all.some(c => c.includes('photo booth'))) return 'Fun for any celebration';
  if (all.some(c => c.includes('bounce') || c.includes('inflat'))) return 'Perfect for outdoor events';
  if (all.some(c => c.includes('face paint'))) return 'Great for kids\' events';
  if (all.some(c => c.includes('music'))) return 'Sets the mood for your event';
  return null;
};

// Vendor availability check against event date
// Returns { status: 'available'|'unavailable'|'unknown', label: string } or null if no date
const getVendorAvailability = (vendor, eventDate) => {
  if (!eventDate) return null;

  const evDate = new Date(eventDate + 'T00:00:00');
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = evDate.getTime() - today.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  // Short date label: "Aug 12"
  const shortDate = evDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const blocked = vendor.blocked_dates || [];
  const hasScheduleData = blocked.length > 0 || vendor.accepts_same_day != null || vendor.allow_next_day != null || vendor.min_notice_hours > 0;

  // Check blocked dates
  if (blocked.includes(eventDate)) {
    return { status: 'unavailable', label: `Unavailable on ${shortDate}` };
  }

  // Check same-day
  if (diffDays < 1 && vendor.accepts_same_day === false) {
    return { status: 'unavailable', label: `Unavailable for same-day` };
  }

  // Check next-day
  if (diffDays >= 1 && diffDays < 2 && vendor.allow_next_day === false) {
    return { status: 'unavailable', label: `Unavailable for next-day` };
  }

  // Check min notice hours
  if (vendor.min_notice_hours > 0 && diffHours < vendor.min_notice_hours) {
    return { status: 'unavailable', label: 'May not be available — requires advance notice' };
  }

  // If we have schedule data and passed all checks → available
  if (hasScheduleData) {
    return { status: 'available', label: `Available on ${shortDate}` };
  }

  // No schedule data at all → unknown
  return { status: 'unknown', label: 'Availability not confirmed' };
};

// Get displayable price from vendor - handles various price formats
const getVendorPrice = (vendor) => {
  // Check for numeric starting_price or base_price first
  if (typeof vendor.starting_price === 'number' && vendor.starting_price > 0) {
    return { display: formatCurrency(vendor.starting_price), numeric: vendor.starting_price };
  }
  if (typeof vendor.base_price === 'number' && vendor.base_price > 0) {
    return { display: formatCurrency(vendor.base_price), numeric: vendor.base_price };
  }
  
  // Handle array format (e.g., ['$500 - $1000'])
  if (Array.isArray(vendor.starting_price) && vendor.starting_price.length > 0) {
    const priceStr = vendor.starting_price[0];
    if (typeof priceStr === 'string' && priceStr.length > 0) {
      // Extract first number for calculation
      const match = priceStr.match(/\$?([\d,]+)/);
      const numeric = match ? parseInt(match[1].replace(/,/g, '')) : null;
      return { display: priceStr, numeric };
    }
  }
  
  // Handle string format
  if (typeof vendor.starting_price === 'string' && vendor.starting_price.length > 0) {
    const match = vendor.starting_price.match(/\$?([\d,]+)/);
    const numeric = match ? parseInt(match[1].replace(/,/g, '')) : null;
    return { display: vendor.starting_price, numeric };
  }
  
  return { display: null, numeric: null };
};

// ─── Planner snapshot persistence (Feb 2026) ─────────────────────────────
// When a user taps "View Details" or a gallery thumbnail from the planner,
// PlanEvent unmounts and its useState is lost. We persist eventData +
// currentStep to sessionStorage so PlanEvent can rehydrate when the user
// returns. sessionStorage scope = per-tab, auto-cleared on tab close.
// Distinct from localStorage.eventDraft (auth-gate flow) by key + lifetime.
const PLANNER_SNAPSHOT_KEY = 'plannerSnapshot';
const PLANNER_SNAPSHOT_TTL_MS = 60 * 60 * 1000; // 60 minutes

const savePlannerSnapshotRaw = (eventData, currentStep) => {
  try {
    sessionStorage.setItem(PLANNER_SNAPSHOT_KEY, JSON.stringify({
      eventData,
      currentStep,
      savedAt: Date.now(),
    }));
  } catch { /* sessionStorage full / unavailable — best-effort only */ }
};

const PlanEvent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const appLoc = useAppLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [sentInquiryCount, setSentInquiryCount] = useState(0);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [previewVendor, setPreviewVendor] = useState(null);
  const [previewBadges, setPreviewBadges] = useState([]);
  const [typeSearch, setTypeSearch] = useState('');
  const eventTypeSearchRef = useRef(null);
  // Step 1 More-tile state. Tapping "More…" expands a secondary list of
  // canonical event types BELOW the primary tile grid (not just focus the
  // search bar) and surfaces the existing custom-event affordance. Auto-
  // opens whenever the customer has a custom event_type already selected
  // (e.g. a restored draft) so the chip is reachable for editing.
  const [showMoreTypes, setShowMoreTypes] = useState(false);
  const [showNotListed, setShowNotListed] = useState(false);
  const [customEventType, setCustomEventType] = useState('');
  const [officialTypes, setOfficialTypes] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceCategories, setServiceCategories] = useState([]);
  const [browseAllVendors, setBrowseAllVendors] = useState([]);
  const [showBrowseAll, setShowBrowseAll] = useState(false);
  const [loadingBrowseAll, setLoadingBrowseAll] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  
  // Event data
  const [eventData, setEventData] = useState({
    event_type: '',
    event_date: new Date().toISOString().split('T')[0],
    event_time: '',
    budget: '',
    guest_count: '',
    location_zip: '',
    location_city: '',
    location_state: '',
    location_lat: null,
    location_lng: null,
    selected_services: [],
    selected_vendors: [],
    customer_message: ''
  });
  
  // ─── Prefill location from shared LocationContext ───
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current) return;
    // Don't overwrite a restored draft
    if (eventData.location_zip || eventData.location_city) return;
    if (appLoc.hasLocation) {
      prefillDone.current = true;
      setEventData(prev => ({
        ...prev,
        location_zip: appLoc.location.zip || prev.location_zip,
        location_city: appLoc.location.city || prev.location_city,
        location_state: appLoc.location.state || prev.location_state,
        location_lat: appLoc.location.lat ?? prev.location_lat,
        location_lng: appLoc.location.lng ?? prev.location_lng,
      }));
    }
  }, [appLoc.hasLocation, appLoc.location]);

  // Detect location helper for PlanEvent
  const handleDetectEventLocation = async () => {
    const result = await appLoc.detectLocation();
    if (result) {
      setEventData(prev => ({
        ...prev,
        location_zip: result.zip || prev.location_zip,
        location_city: result.city || prev.location_city,
        location_state: result.state || prev.location_state,
        location_lat: result.lat ?? prev.location_lat,
        location_lng: result.lng ?? prev.location_lng,
      }));
    }
  };
  
  // Fetch vendors when services are selected AND zip is entered
  const fetchVendorsForServices = useCallback(async () => {
    if (eventData.selected_services.length === 0) {
      setVendors([]);
      return;
    }
    
    // Location fallback: ZIP > city > state
    const zip = eventData.location_zip.trim();
    const city = eventData.location_city.trim();
    const state = eventData.location_state.trim();
    const hasZip = /^\d{5}$/.test(zip);
    if (!hasZip && !city && !state) {
      setVendors([]);
      return;
    }
    
    setLoadingVendors(true);
    try {
      const token = localStorage.getItem('token');
      const allVendors = [];
      
      for (const category of eventData.selected_services) {
        const params = { category };
        if (hasZip) params.zip_code = zip;
        else if (city) params.city = city;
        else if (state) {
          params.state = state;
          params.limit = 20;
        }
        
        const response = await axios.get(`${API}/vendors`, {
          params,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        const vendorsWithCategory = (response.data || []).map(v => ({
          ...v,
          // Phase 5C display fix: preserve the vendor's true identity on
          // the Plan Event surface. `display_category` is server-computed
          // from primary_service / categories[0]; falling back to the
          // queried `category` only when a vendor doc has neither.
          matched_category: v.display_category || v.categories?.[0] || category
        }));
        allVendors.push(...vendorsWithCategory);
      }
      
      // Remove duplicates
      const uniqueVendors = allVendors.reduce((acc, vendor) => {
        const existing = acc.find(v => v.id === vendor.id);
        if (!existing) acc.push(vendor);
        return acc;
      }, []);
      
      setVendors(uniqueVendors);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoadingVendors(false);
    }
  }, [eventData.selected_services, eventData.location_zip, eventData.location_city, eventData.location_state]);

  // ─── Decision Reduction Engine (V1 rule-based) ──────────────────────
  // First time the customer arrives at Step 2 with an empty service list,
  // seed `selected_services` with the template essentials for their chosen
  // event_type. This intentionally does NOT re-seed on subsequent visits
  // (so customer removals stick) and does NOT overwrite restored drafts
  // or deep-link seed-category arrivals (which already populated the
  // list before Step 2 mounted).
  const templateSeeded = useRef(false);
  useEffect(() => {
    if (currentStep !== 2) return;
    if (templateSeeded.current) return;
    if (!eventData.event_type) return;
    if (eventData.selected_services.length > 0) {
      // Restored draft or seed-category deep link already populated the
      // list — mark as seeded so we never overwrite it.
      templateSeeded.current = true;
      return;
    }
    const tpl = getTemplateForEventType(eventData.event_type);
    if (!tpl.essentials || tpl.essentials.length === 0) {
      templateSeeded.current = true;
      return;
    }
    setEventData((prev) => {
      if (prev.selected_services.length > 0) return prev; // race-safety
      return { ...prev, selected_services: [...tpl.essentials] };
    });
    templateSeeded.current = true;
  }, [currentStep, eventData.event_type, eventData.selected_services.length]);

  const fetchBrowseAllVendors = useCallback(async () => {
    const zip = eventData.location_zip.trim();
    const city = eventData.location_city.trim();
    const state = eventData.location_state.trim();
    const hasZip = /^\d{5}$/.test(zip);
    if (!hasZip && !city && !state) return;
    
    setLoadingBrowseAll(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (hasZip) params.zip_code = zip;
      else if (city) params.city = city;
      else if (state) {
        params.state = state;
        params.limit = 20;
      }
      
      const response = await axios.get(`${API}/vendors`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const existingIds = new Set(vendors.map(v => v.id));
      const additional = (response.data || []).filter(v => !existingIds.has(v.id));
      setBrowseAllVendors(additional);
    } catch (error) {
      console.error('Error fetching all vendors:', error);
    } finally {
      setLoadingBrowseAll(false);
    }
  }, [eventData.location_zip, eventData.location_city, eventData.location_state, vendors]);

  // Restore saved draft if returning from auth
  // Restore draft from landing page (works for logged-out users)
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem('eventDraft');
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        if (draft.eventData) {
          setEventData(draft.eventData);
          if (draft.draftVersion === 3 && draft.currentStep) setCurrentStep(draft.currentStep);
          localStorage.removeItem('eventDraft');
          if (user) {
            localStorage.removeItem('authRedirect');
            toast.success('Your event draft has been restored.');
          }
        }
      }
    } catch {
      localStorage.removeItem('eventDraft');
    }
  }, [user]);

  // Hydrate planner snapshot when returning from VendorDetail (Feb 2026).
  // Runs once on mount; deletes the snapshot after use so a future fresh
  // "Start Planning" entry doesn't accidentally re-load stale state.
  useEffect(() => {
    try {
      const snap = sessionStorage.getItem(PLANNER_SNAPSHOT_KEY);
      if (!snap) return;
      const parsed = JSON.parse(snap);
      if (!parsed?.savedAt || Date.now() - parsed.savedAt > PLANNER_SNAPSHOT_TTL_MS) {
        sessionStorage.removeItem(PLANNER_SNAPSHOT_KEY);
        return;
      }
      if (parsed.eventData) setEventData(parsed.eventData);
      if (typeof parsed.currentStep === 'number') setCurrentStep(parsed.currentStep);
      sessionStorage.removeItem(PLANNER_SNAPSHOT_KEY);
    } catch {
      try { sessionStorage.removeItem(PLANNER_SNAPSHOT_KEY); } catch { /* noop */ }
    }
    // Intentionally run once on mount only.
  }, []);

  // ─── Vendor-seeded entry (Mode B, Feb 2026) ────────────────────────
  // When the user taps "Create Event" from a vendor profile, the URL
  // arrives with seedVendorId / seedCategory / seedVendorName /
  // seedStartingPrice. We merge those into eventData on mount, AFTER
  // eventDraft + plannerSnapshot hydration so resumed drafts always win.
  // Then strip the params from the URL so a refresh / back nav cannot
  // re-seed (would double-add otherwise). Idempotent — re-entry from
  // the same vendor is a no-op.
  useEffect(() => {
    const seedVendorId = searchParams.get('seedVendorId');
    if (!seedVendorId) return;
    const seedCategory = searchParams.get('seedCategory') || 'Service';
    const seedVendorName = searchParams.get('seedVendorName') || 'Vendor';
    const seedStartingPriceRaw = searchParams.get('seedStartingPrice');
    const seedStartingPrice = seedStartingPriceRaw ? Number(seedStartingPriceRaw) : null;
    // Avatar fallback chain — see VendorDetail "Create event" CTA. Both
    // optional; getVendorAvatar() handles missing/empty gracefully.
    const seedProfilePhoto = searchParams.get('seedProfilePhoto') || null;
    const seedOwnerPhoto = searchParams.get('seedOwnerPhoto') || null;

    // Inquiry-form draft carry-over (Feb 2026). Each field is merged
    // ONLY if eventData's own field is still empty so a resumed draft
    // (eventDraft / plannerSnapshot) always wins.
    const seedEventType = searchParams.get('seedEventType');
    const seedEventDate = searchParams.get('seedEventDate');
    const seedEventTime = searchParams.get('seedEventTime');
    const seedLocation = searchParams.get('seedLocation');
    const seedGuestCount = searchParams.get('seedGuestCount');
    const seedBudget = searchParams.get('seedBudget');

    let didAddVendor = false;
    let didAddService = false;
    setEventData((prev) => {
      const serviceMissing = seedCategory && !prev.selected_services.includes(seedCategory);
      const vendorMissing = !prev.selected_vendors.some((v) => v.vendor_id === seedVendorId);
      didAddVendor = vendorMissing;
      didAddService = serviceMissing;
      const draftMerge = {
        ...(seedEventType && !prev.event_type ? { event_type: seedEventType } : {}),
        ...(seedEventDate && !prev.event_date ? { event_date: seedEventDate } : {}),
        ...(seedEventTime && !prev.event_time ? { event_time: seedEventTime } : {}),
        ...(seedLocation && !prev.location_city ? { location_city: seedLocation } : {}),
        ...(seedGuestCount && !prev.guest_count ? { guest_count: seedGuestCount } : {}),
        ...(seedBudget && !prev.budget ? { budget: seedBudget } : {}),
      };
      const hasDraftMerge = Object.keys(draftMerge).length > 0;
      if (!serviceMissing && !vendorMissing && !hasDraftMerge) return prev;
      return {
        ...prev,
        ...draftMerge,
        selected_services: serviceMissing ? [...prev.selected_services, seedCategory] : prev.selected_services,
        selected_vendors: vendorMissing
          ? [
              ...prev.selected_vendors,
              {
                vendor_id: seedVendorId,
                vendor_name: seedVendorName,
                category: seedCategory,
                starting_price: seedStartingPrice,
                starting_price_display: seedStartingPrice ? `$${seedStartingPrice}` : null,
                profile_photo: seedProfilePhoto,
                owner_photo: seedOwnerPhoto,
              },
            ]
          : prev.selected_vendors,
      };
    });

    // Advance to Step 2 only if user is still on Step 1 (no resumed draft)
    setCurrentStep((s) => (s === 1 ? 2 : s));

    // Strip seed params so refresh / browser-back cannot re-seed
    try {
      const url = new URL(window.location.href);
      ['seedVendorId', 'seedCategory', 'seedVendorName', 'seedStartingPrice',
        'seedProfilePhoto', 'seedOwnerPhoto',
        'seedEventType', 'seedEventDate', 'seedEventTime', 'seedLocation',
        'seedGuestCount', 'seedBudget'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
    } catch { /* noop */ }

    // Toast only when something actually changed
    if (didAddVendor || didAddService) {
      toast.success(`${seedCategory} (${seedVendorName}) added to your plan`);
    }
  }, []);

  // Fetch official event types and service categories from backend
  useEffect(() => {
    axios.get(`${API}/event-types`).then(res => {
      setOfficialTypes(res.data.types || []);
    }).catch(() => {});
    axios.get(`${API}/categories`).then(res => {
      setServiceCategories((res.data.categories || []).map(c => ({ value: c })));
    }).catch(() => {});
  }, []);
  
  useEffect(() => {
    if (currentStep === 4) {
      fetchVendorsForServices();
    }
  }, [currentStep, fetchVendorsForServices]);
  
  // Re-fetch when ZIP changes on step 4 (vendor selection)
  useEffect(() => {
    if (currentStep === 4 && /^\d{5}$/.test(eventData.location_zip.trim())) {
      fetchVendorsForServices();
    }
  }, [eventData.location_zip]);

  // Auto-expand browse-all when matched vendors load on Step 4
  useEffect(() => {
    if (currentStep === 4 && !loadingVendors && vendors.length > 0 && !showBrowseAll) {
      setShowBrowseAll(true);
      fetchBrowseAllVendors();
    }
  }, [currentStep, loadingVendors, vendors.length]);
  
  // Calculate estimated total
  const estimatedTotal = eventData.selected_vendors.reduce((sum, v) => {
    const price = v.starting_price;
    if (price && typeof price === 'number' && price > 0) {
      return sum + price;
    }
    return sum;
  }, 0);
  
  // Count vendors with TBD pricing
  const tbdCount = eventData.selected_vendors.filter(v => !v.starting_price || v.starting_price <= 0).length;
  
  // Toggle vendor selection
  const toggleVendor = (vendor) => {
    const isSelected = eventData.selected_vendors.some(v => v.vendor_id === vendor.id);
    
    if (isSelected) {
      setEventData(prev => ({
        ...prev,
        selected_vendors: prev.selected_vendors.filter(v => v.vendor_id !== vendor.id)
      }));
    } else {
      const priceInfo = getVendorPrice(vendor);
      setEventData(prev => ({
        ...prev,
        selected_vendors: [...prev.selected_vendors, {
          vendor_id: vendor.id,
          vendor_name: vendor.business_name,
          category: vendor.matched_category || vendor.categories?.[0] || 'Service',
          starting_price: priceInfo.numeric, // Store numeric value for calculations
          starting_price_display: priceInfo.display, // Store display string
          profile_photo: vendor.profile_photo,
          owner_photo: vendor.owner_photo,
        }]
      }));
    }
  };
  
  // Toggle service selection
  const toggleService = (serviceValue) => {
    setEventData(prev => {
      const isSelected = prev.selected_services.includes(serviceValue);
      if (isSelected) {
        return {
          ...prev,
          selected_services: prev.selected_services.filter(s => s !== serviceValue),
          // Also remove vendors from this category
          selected_vendors: prev.selected_vendors.filter(v => v.category !== serviceValue)
        };
      } else {
        return {
          ...prev,
          selected_services: [...prev.selected_services, serviceValue]
        };
      }
    });
  };
  
  // ZIP auto-fill city lookup
  const lookupZip = useCallback(async (zip) => {
    if (!/^\d{5}$/.test(zip)) return;
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          const city = place['place name'];
          const state = place['state abbreviation'];
          setEventData(prev => ({
            ...prev,
            location_city: city,
            location_state: state,
          }));
          // Note: intentionally do NOT propagate to appLoc (shared context).
          // The planner's location should not silently become the app-wide
          // Browse filter — Browse manages its own location via Detect /
          // saved preference / manual entry. See audit 2026-02-28.
        }
      }
    } catch {
      // Lookup failed — user can enter city manually
    }
  }, []);

  // Send event plan
  const sendEventPlan = async () => {
    // Validate date is not in the past
    if (eventData.event_date < today) {
      toast.error('Event date must be today or later.');
      return;
    }
    
    const hasVendors = eventData.selected_vendors.length > 0;
    const zip = eventData.location_zip.trim();
    const city = eventData.location_city.trim();
    
    // Location is only required when sending to vendors
    if (hasVendors) {
      if (!zip && !city) {
        toast.error('Add a location so we can match you with nearby vendors before sending.');
        setCurrentStep(3);
        return;
      }
      if (zip && !/^\d{5}(-\d{4})?$/.test(zip)) {
        toast.error('Please enter a valid ZIP code (e.g., 78701)');
        setCurrentStep(3);
        return;
      }
    }
    
    // Soft auth gate: if not logged in, save draft and show auth modal
    if (!user) {
      try {
        localStorage.setItem('eventDraft', JSON.stringify({
          eventData,
          currentStep,
          draftVersion: 3,
        }));
      } catch { /* localStorage full — modal still shows */ }
      setShowAuthGate(true);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create event plan
      const createResponse = await axios.post(`${API}/events`, {
        event_type: PRIMARY_EVENT_TYPES.find(t => t.value === eventData.event_type)?.label || eventData.event_type,
        event_date: eventData.event_date,
        event_time: eventData.event_time || null,
        budget: eventData.budget ? parseFloat(eventData.budget) : null,
        guest_count: eventData.guest_count ? parseInt(eventData.guest_count) : null,
        location_zip: eventData.location_zip || null,
        location_city: eventData.location_city || null,
        selected_services: eventData.selected_services,
        selected_vendors: eventData.selected_vendors,
        customer_message: eventData.customer_message || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const eventId = createResponse.data.id;
      setCreatedEventId(eventId);
      
      // Track custom event type if not in primary list
      if (!PRIMARY_EVENT_TYPES.find(t => t.value === eventData.event_type)) {
        axios.post(`${API}/event-types/track`, { name: eventData.event_type }, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
      
      // Only send inquiries if vendors were selected
      if (hasVendors) {
        const sendResponse = await axios.post(`${API}/events/${eventId}/send`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSentInquiryCount(sendResponse.data.successful_sends || eventData.selected_vendors.length);
      } else {
        setSentInquiryCount(0);
      }
      
      setShowSuccess(true);
      
      // Haptic feedback on mobile
      if (navigator.vibrate) {
        navigator.vibrate([50, 30, 50]);
      }
      
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };
  
  const today = new Date().toISOString().split('T')[0];
  
  // Navigation helpers
  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!eventData.event_type;
      case 2: return eventData.selected_services.length > 0;
      case 3: return !!eventData.event_date && eventData.event_date >= today;
      case 4: return true; // Vendors optional — can be added later
      case 5: return true; // Review step
      default: return false;
    }
  };
  
  const nextStep = () => {
    if (currentStep === 3 && eventData.event_date < today) {
      toast.error('Event date must be today or later.');
      return;
    }
    if (canProceed() && currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Check if user has made any progress worth confirming
  const hasProgress = () => {
    return (
      eventData.event_type ||
      eventData.event_date ||
      eventData.selected_services.length > 0 ||
      eventData.selected_vendors.length > 0
    );
  };
  
  // Handle exit workflow
  const handleExit = () => {
    if (hasProgress()) {
      setShowExitDialog(true);
    } else {
      navigate(user ? '/customer-dashboard' : '/');
    }
  };
  
  // Confirm exit
  const confirmExit = () => {
    setShowExitDialog(false);
    navigate(user ? '/customer-dashboard' : '/');
  };
  
  // Get image URL helper
  const getImageUrl = (imagePath) => {
    if (!imagePath || imagePath.trim() === '') return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${backendUrl}${cleanPath}`;
  };

  // Fetch badges when preview vendor changes
  useEffect(() => {
    if (!previewVendor) { setPreviewBadges([]); return; }
    const fetchBadges = async () => {
      try {
        const res = await axios.get(`${API}/vendors/${previewVendor.id}/badges`);
        setPreviewBadges(res.data || []);
      } catch { setPreviewBadges([]); }
    };
    fetchBadges();
  }, [previewVendor]);
  
  // Show loading while auth is checking
  if (authLoading) {
    return <CeleyPageLoader text="Preparing your event planner..." />;
  }
  
  // Success screen
  if (showSuccess) {
    const hasVendors = sentInquiryCount > 0;
    const successTheme = getEventTheme(eventData.event_type);
    const successAccent = successTheme.accentColor;
    const successCopy = getEventCopy(eventData.event_type);
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: successTheme.gradient }}>
        <div className="relative text-center max-w-md animate-in fade-in-0 zoom-in-95 duration-500 overflow-hidden">
          {/* Celebration particles — unified delight system */}
          <CeleyDelightLayer mode="celebration" mood={successTheme.mood} />
          {/* Success animation */}
          <div className="relative mb-6">
            <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center animate-pulse" style={{ backgroundColor: successAccent + '18' }}>
              <CheckCircle2 className="w-12 h-12" style={{ color: successAccent }} />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="success-title">
            {hasVendors ? successCopy.headline : 'Event Created'}
          </h1>
          <p className="text-gray-600 mb-6" data-testid="success-message">
            {hasVendors 
              ? successCopy.sub + `. Your plan has been sent to ${sentInquiryCount} vendor${sentInquiryCount !== 1 ? 's' : ''} — they'll respond soon.`
              : 'Your event has been saved. You can add vendors anytime and send requests when ready.'
            }
          </p>
          
          <div className="space-y-3">
            <Button 
              onClick={() => navigate(`/events/${createdEventId}`)}
              className="w-full text-white"
              style={{ backgroundColor: successAccent }}
              data-testid="view-event-details-btn"
            >
              {hasVendors ? 'View Event Details' : 'View My Event'}
            </Button>
            {hasVendors ? (
              <Button 
                variant="outline"
                onClick={() => navigate('/customer-dashboard')}
                className="w-full"
                data-testid="back-to-home-btn"
              >
                Back to Home
              </Button>
            ) : (
              <Button 
                variant="outline"
                onClick={() => navigate('/browse')}
                className="w-full"
                data-testid="browse-vendors-btn"
              >
                Browse Vendors
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Navigation hideMobileNav={true} />
      
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-lg md:max-w-xl mx-auto px-5 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => currentStep > 1 ? prevStep() : handleExit()}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={currentStep > 1 ? "Go back" : "Exit"}
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-gray-900">Plan My Event</h1>
              <p className="text-sm text-gray-500">Step {currentStep} of 5</p>
            </div>
            {/* Quick exit button */}
            <button 
              onClick={handleExit}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Exit event planning"
              data-testid="exit-plan-event-btn"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${(currentStep / 5) * 100}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-lg md:max-w-xl mx-auto px-5 pt-6 pb-52">
        
        {/* Step 1: Event Type */}
        {currentStep === 1 && (() => {
          const q = typeSearch.trim().toLowerCase();
          // Merge primary labels + official from backend (deduplicated)
          const allOfficialList = [...new Set([
            ...PRIMARY_EVENT_TYPES.map(t => t.value),
            ...officialTypes
          ])];
          // Filter by search query
          const filtered = q
            ? allOfficialList.filter(t => t.toLowerCase().includes(q))
            : [];
          const exactMatch = q && allOfficialList.some(t => t.toLowerCase() === q);

          return (
          <div className="space-y-4" data-testid="step-event-type">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">What are you planning?</h2>
              <p className="text-gray-500 mt-1">Select your event type</p>
            </div>

            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search event type..."
                value={typeSearch}
                onChange={(e) => { setTypeSearch(e.target.value); setShowNotListed(false); }}
                className="pl-10 bg-white"
                ref={eventTypeSearchRef}
                data-testid="event-type-search"
              />
            </div>

            {/* Search results */}
            {q ? (
              <div className="space-y-2" data-testid="event-type-search-results">
                {filtered.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {filtered.map(type => {
                      const isSelected = eventData.event_type === type;
                      return (
                        <button
                          key={type}
                          onClick={() => {
                            setEventData(prev => ({ ...prev, event_type: type }));
                            setTypeSearch('');
                            setShowNotListed(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-emerald-500 text-white shadow-sm'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-200'
                          }`}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {/* Custom event creation card — surfaces when the typed
                    query has no exact match in the canonical event-type
                    catalog. Worded to explain what will happen rather
                    than relying on jargon like "(custom)". */}
                {!exactMatch && (
                  <button
                    onClick={() => {
                      setEventData(prev => ({ ...prev, event_type: typeSearch.trim() }));
                      setTypeSearch('');
                      setShowNotListed(false);
                      // Collapse the expanded "Looking for something else?"
                      // section so the new confirmation card is the single
                      // visible confirmation surface.
                      setShowMoreTypes(false);
                    }}
                    className="w-full text-left px-4 py-3 rounded-xl bg-white border-2 border-emerald-200 hover:border-emerald-400 transition-colors"
                    data-testid="event-type-search-custom"
                  >
                    <p className="text-sm text-gray-500 mb-1">No matching event found.</p>
                    <p className="text-sm text-gray-600 mb-1">Create a custom event:</p>
                    <p className="text-base font-semibold text-emerald-700 mb-3">
                      {typeSearch.trim()}
                    </p>
                    <div className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium">
                      Continue with Custom Event
                    </div>
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Primary tiles — full master registry, with "More…" as the
                    final UI affordance that focuses the search input above.
                    "More…" is NEVER selected as an event_type. */}
                <div className="grid grid-cols-2 gap-3">
                  {PRIMARY_EVENT_TYPES.map(type => {
                    const Icon = type.icon;
                    if (type.isMore) {
                      const isMoreActive = showMoreTypes || (
                        eventData.event_type
                        && !PRIMARY_EVENT_TYPES.find(t => t.value === eventData.event_type)
                      );
                      return (
                        <button
                          key={type.value}
                          onClick={() => {
                            setShowNotListed(false);
                            setShowMoreTypes(true);
                            // Intentionally DO NOT focus the search input
                            // here — focusing opens the mobile keyboard
                            // and scrolls the viewport up, hiding the
                            // newly-revealed More Event Types section.
                            // The search input is only focused when the
                            // customer explicitly taps the search field
                            // or the "Type your event above" link.
                          }}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            isMoreActive
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-gray-200 bg-white hover:border-emerald-200'
                          }`}
                          data-testid="event-type-more"
                        >
                          <Icon className={`w-6 h-6 mb-2 ${isMoreActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                          <p className={`font-medium ${isMoreActive ? 'text-emerald-900' : 'text-gray-700'}`}>
                            {type.label}
                          </p>
                        </button>
                      );
                    }
                    const isSelected = eventData.event_type === type.value;
                    return (
                      <button
                        key={type.value}
                        onClick={() => {
                          setEventData(prev => ({ ...prev, event_type: type.value }));
                          setShowNotListed(false);
                        }}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 bg-white hover:border-emerald-200'
                        }`}
                        data-testid={`event-type-${type.value.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <p className={`font-medium ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                          {type.label}
                        </p>
                      </button>
                    );
                  })}
                </div>

                {/* Expanded event types — revealed when the customer taps
                    "More…" or when a custom event_type is already selected.
                    All values are exact canonical backend strings; safe to
                    feed straight into existing tracking + Decision
                    Reduction Engine (fallback template handles unmapped
                    values). */}
                {/* Expanded "Looking for something else?" section is
                    revealed ONLY when the customer explicitly taps the
                    More… tile. When a custom event is already selected,
                    the single confirmation card below is the canonical
                    confirmation surface — we don't ALSO show this list. */}
                {showMoreTypes && (
                  <div
                    className="space-y-2 pt-2"
                    data-testid="event-type-expanded"
                  >
                    <p className="text-sm font-medium text-gray-600">
                      Looking for something else?
                    </p>
                    {/* Visually secondary list — two-column compact grid.
                        Tiles allow long labels (e.g. "Memorial / Celebration
                        of Life", "Quinceañera / Sweet 16", "Conference /
                        Seminar") to wrap inside their cell, preserving the
                        exact canonical event_type string while keeping
                        vertical scroll short on mobile. */}
                    <div className="grid grid-cols-2 gap-2">
                      {EXPANDED_EVENT_TYPES.map(type => {
                        const isSelected = eventData.event_type === type.value;
                        return (
                          <button
                            key={type.value}
                            onClick={() => {
                              setEventData(prev => ({ ...prev, event_type: type.value }));
                              setShowNotListed(false);
                            }}
                            className={`min-h-[52px] flex items-center justify-center text-center px-3 py-2.5 rounded-xl border transition-all ${
                              isSelected
                                ? 'bg-emerald-50 border-emerald-500'
                                : 'bg-white border-gray-200 hover:border-emerald-200 active:bg-gray-50'
                            }`}
                            data-testid={`event-type-expanded-${type.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                          >
                            <span className={`text-xs sm:text-sm font-medium leading-snug break-words ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                              {type.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 pt-1" data-testid="event-type-custom-hint">
                      Don&apos;t see your event?{' '}
                      <button
                        type="button"
                        onClick={() => eventTypeSearchRef.current?.focus()}
                        className="text-emerald-700 underline font-medium bg-transparent border-0 p-0 cursor-pointer"
                        data-testid="event-type-custom-cta"
                      >
                        Start typing above
                      </button>
                      —we support hundreds of event types, and you can always create your own.
                    </p>
                  </div>
                )}

                {/* Single confirmation card — surfaces when a custom
                    event_type is selected (i.e. not in the visible primary
                    registry). The expanded "Looking for something else?"
                    section above is for browsing alternatives; this card
                    is the one canonical confirmation surface for the
                    customer's chosen custom event. Tapping it re-focuses
                    the search field so the customer can change it. */}
                {eventData.event_type && !PRIMARY_EVENT_TYPES.find(t => t.value === eventData.event_type) && !EXPANDED_EVENT_TYPES.find(t => t.value === eventData.event_type) && (
                  <button
                    onClick={() => eventTypeSearchRef.current?.focus()}
                    className="w-full p-4 rounded-xl border-2 border-emerald-500 bg-emerald-50 text-left transition-all"
                    data-testid="event-type-custom-selected"
                  >
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">
                          Custom Event Selected
                        </p>
                        <p className="text-base font-semibold text-emerald-900 mt-0.5 break-words">
                          {eventData.event_type}
                        </p>
                        <p className="text-xs text-emerald-700/70 mt-1">Tap to change</p>
                      </div>
                    </div>
                  </button>
                )}
              </>
            )}

            {/* Current selection indicator */}
            {eventData.event_type && !q && (
              <div className="flex items-center justify-center gap-2 pt-2 text-sm text-emerald-600">
                <Check className="w-4 h-4" />
                <span>Selected: <strong>{eventData.event_type}</strong></span>
              </div>
            )}
          </div>
          );
        })()}
        
        {/* Step 2: Services Needed */}
        {currentStep === 2 && (() => {
          const sq = serviceSearch.trim().toLowerCase();
          const searchResults = sq
            ? serviceCategories.filter(s => s.value.toLowerCase().includes(sq))
            : [];

          return (
          <div className="space-y-4" data-testid="step-services">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">What services do you need?</h2>
              <p className="text-gray-500 mt-1">Select all that apply</p>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search services..."
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                className="pl-10 bg-white"
                data-testid="service-search"
              />
            </div>
            
            {sq ? (
              searchResults.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {searchResults.map(service => {
                    const isSelected = eventData.selected_services.includes(service.value);
                    return (
                      <button
                        key={service.value}
                        onClick={() => toggleService(service.value)}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-gray-200 bg-white hover:border-emerald-200'
                        }`}
                        data-testid={`service-${service.value}`}
                      >
                        <Sparkles className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <p className={`text-sm font-medium ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                          {service.value}
                        </p>
                        {isSelected && <Check className="w-4 h-4 text-emerald-600 mx-auto mt-1" />}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center" data-testid="service-search-empty">
                  <p className="text-gray-500 text-sm">No services found</p>
                  <p className="text-gray-400 text-xs mt-1">Try another search</p>
                </div>
              )
            ) : (() => {
              const tpl = getTemplateForEventType(eventData.event_type);
              const essentialSet = new Set(tpl.essentials);
              const addOnSet = new Set(tpl.addOns);
              // Essentials surface = template essentials, in template order.
              // Render every essential even if the customer has toggled it
              // OFF, so they can re-add it without searching.
              const essentialTiles = tpl.essentials
                .map((value) => POPULAR_SERVICES.find((s) => s.value === value) || { value, label: value, icon: Sparkles });
              // Add-on surface = template add-ons UNION any
              // POPULAR_SERVICES tiles not already in essentials. Keeps the
              // 17-tile breadth available without duplicating essentials.
              const addOnTiles = [
                ...tpl.addOns.map((value) => POPULAR_SERVICES.find((s) => s.value === value) || { value, label: value, icon: Sparkles }),
                ...POPULAR_SERVICES.filter((s) => !essentialSet.has(s.value) && !addOnSet.has(s.value)),
              ];

              const renderTile = (service) => {
                const Icon = service.icon || Sparkles;
                const isSelected = eventData.selected_services.includes(service.value);
                return (
                  <button
                    key={service.value}
                    onClick={() => toggleService(service.value)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 bg-white hover:border-emerald-200'
                    }`}
                    data-testid={`service-${service.value}`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                      {service.label}
                    </p>
                    {isSelected && <Check className="w-4 h-4 text-emerald-600 mx-auto mt-1" />}
                  </button>
                );
              };

              return (
                <div className="space-y-6">
                  {/* Event Essentials — Celey has already started the checklist */}
                  {essentialTiles.length > 0 && (
                    <div data-testid="step-services-essentials">
                      <div className="flex items-baseline justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-900">Event Essentials</h3>
                        <span className="text-xs text-gray-400">Already selected · tap to remove</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {essentialTiles.map(renderTile)}
                      </div>
                    </div>
                  )}

                  {/* Popular Add-ons — optional */}
                  {addOnTiles.length > 0 && (
                    <div data-testid="step-services-addons">
                      <div className="flex items-baseline justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-900">Popular Add-ons</h3>
                        <span className="text-xs text-gray-400">Optional</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {addOnTiles.map(renderTile)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {eventData.selected_services.length > 0 && (
              <p className="text-center text-sm text-emerald-600 font-medium">
                {eventData.selected_services.length} service{eventData.selected_services.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          );
        })()}
        
        {/* Step 3: Event Details */}
        {currentStep === 3 && (
          <div className="space-y-4" data-testid="step-event-details">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900">Event Details</h2>
              <p className="text-gray-500 mt-1 text-sm">Help vendors give you accurate quotes</p>
            </div>

            {/* Section: When */}
            <div className="space-y-2" data-testid="section-when">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">When</p>
              <div className="flex gap-2">
                <div className="flex-[2] min-w-0 relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    id="event_date"
                    type="date"
                    value={eventData.event_date}
                    onChange={(e) => setEventData(prev => ({ ...prev, event_date: e.target.value }))}
                    min={today}
                    className="pl-10 w-full"
                    data-testid="event-date-input"
                  />
                </div>
                <div className="flex-1 min-w-[100px]">
                  <TimePicker
                    id="event_time"
                    value={eventData.event_time}
                    onChange={(e) => setEventData(prev => ({ ...prev, event_time: e.target.value }))}
                    placeholder="Time"
                    data-testid="event-time-input"
                  />
                </div>
              </div>
              {eventData.event_date && eventData.event_date < today && (
                <p className="text-xs text-red-500" data-testid="past-date-error">Event date must be today or later.</p>
              )}
            </div>

            <div className="border-t border-gray-100" />

            {/* Section: Where */}
            <div className="space-y-2" data-testid="section-where">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Where</p>
                {!eventData.location_city && !eventData.location_zip && appLoc.geoStatus !== 'detecting' && (
                  <button
                    type="button"
                    onClick={handleDetectEventLocation}
                    className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                    data-testid="step3-detect-location-btn"
                  >
                    <Locate className="h-3 w-3" />
                    Detect my location
                  </button>
                )}
                {appLoc.geoStatus === 'detecting' && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 animate-pulse">
                    <Locate className="h-3 w-3 animate-spin" />
                    Detecting...
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  id="location_city"
                  placeholder="City"
                  value={eventData.location_city}
                  onChange={(e) => setEventData(prev => ({ ...prev, location_city: e.target.value }))}
                  data-testid="location-city-input"
                />
                <Input
                  id="location_state"
                  placeholder="State"
                  value={eventData.location_state}
                  maxLength={2}
                  onChange={(e) => setEventData(prev => ({ ...prev, location_state: e.target.value.toUpperCase() }))}
                  data-testid="location-state-input"
                />
                <Input
                  id="location_zip"
                  placeholder="ZIP"
                  value={eventData.location_zip}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setEventData(prev => ({ ...prev, location_zip: val }));
                    if (/^\d{5}$/.test(val.trim())) lookupZip(val.trim());
                  }}
                  data-testid="location-zip-input"
                />
              </div>
              {(eventData.location_city || eventData.location_zip) && (
                <p className="text-xs text-emerald-600">Vendors near your event will be shown</p>
              )}
            </div>

            <div className="border-t border-gray-100" />

            {/* Section: Optional — Guests + Budget side-by-side */}
            <div className="space-y-2" data-testid="section-optional">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Optional</p>
              <p className="text-[11px] text-gray-400 -mt-1">Helps vendors give you more accurate quotes</p>
              <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-3">
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    id="guest_count"
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Guests"
                    value={eventData.guest_count}
                    onChange={(e) => setEventData(prev => ({ ...prev, guest_count: e.target.value }))}
                    className="pl-9"
                    data-testid="guest-count-input"
                  />
                </div>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    id="budget"
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Budget"
                    value={eventData.budget}
                    onChange={(e) => setEventData(prev => ({ ...prev, budget: e.target.value }))}
                    className="pl-9"
                    data-testid="budget-input"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Choose Vendors */}
        {currentStep === 4 && (
          <div className="space-y-4" data-testid="step-vendors">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900">Choose Your Vendors</h2>
              {eventData.selected_vendors.length > 0 && (
                <p className="text-sm text-gray-400 mt-1">
                  {eventData.selected_vendors.length} vendor{eventData.selected_vendors.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>

            {/* Compact context bar — location + services with edit access */}
            <div className="rounded-xl bg-gray-50 border border-gray-100 divide-y divide-gray-100" data-testid="step4-context-bar">
              {/* Location row */}
              <div className="flex items-center justify-between px-4 py-2.5" data-testid="location-summary-panel">
                <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
                  <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
                  {eventData.location_city || eventData.location_zip ? (
                    <span className="font-medium truncate">
                      {[eventData.location_city, eventData.location_state].filter(Boolean).join(', ') || `ZIP ${eventData.location_zip}`}
                    </span>
                  ) : (
                    <span className="text-gray-400">No location set</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium shrink-0 ml-3"
                  data-testid="location-edit-btn"
                >
                  Edit
                </button>
              </div>
              {/* Services row */}
              <div className="flex items-center justify-between px-4 py-2.5" data-testid="services-summary-panel">
                <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
                  <LayoutList className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="truncate">
                    {(() => {
                      const svcs = eventData.selected_services.map(svc => {
                        const cat = POPULAR_SERVICES.find(c => c.value === svc);
                        return cat ? cat.label : svc;
                      });
                      if (svcs.length <= 3) return svcs.join(' · ');
                      return svcs.slice(0, 2).join(' · ') + ` +${svcs.length - 2}`;
                    })()}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium shrink-0 ml-3"
                  data-testid="services-edit-btn"
                >
                  Edit
                </button>
              </div>
            </div>

            {/* Your Selected Vendors — always render when there's at least
                one selected vendor, regardless of location. Discovery
                lists below are deduped to avoid double-rendering the
                same vendor. */}
            {eventData.selected_vendors.length > 0 && (
              <div className="space-y-3" data-testid="step4-selected-vendors">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">Your Selected Vendors</p>
                {eventData.selected_vendors.map(v => {
                  const avatar = getVendorAvatar(v);
                  return (
                    <div
                      key={v.vendor_id}
                      className="rounded-xl border-2 border-emerald-500 bg-emerald-50 overflow-hidden"
                      data-testid={`selected-vendor-card-${v.vendor_id}`}
                    >
                      <div className="flex items-center gap-4 p-4">
                        <div className="w-14 h-14 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-lg shrink-0 overflow-hidden">
                          {avatar ? (
                            <img
                              src={getImageUrl(avatar)}
                              alt={v.vendor_name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            (v.vendor_name || '?').charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{v.vendor_name}</p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">{v.category}</p>
                          <p className={`text-xs font-medium mt-1 ${v.starting_price_display || v.starting_price ? 'text-emerald-600' : 'text-gray-400'}`}>
                            {v.starting_price_display || (v.starting_price ? formatCurrency(v.starting_price) : 'Quote on request')}
                          </p>
                        </div>
                      </div>
                      <div className="flex border-t border-emerald-100 divide-x divide-emerald-100">
                        <button
                          type="button"
                          onClick={() => setEventData(prev => ({
                            ...prev,
                            selected_vendors: prev.selected_vendors.filter(sv => sv.vendor_id !== v.vendor_id)
                          }))}
                          className="flex-1 px-4 py-2.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`selected-vendor-remove-${v.vendor_id}`}
                        >
                          Remove from Plan
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const params = new URLSearchParams();
                            params.set('fromPlan', 'true');
                            if (eventData.event_type) params.set('planEventType', eventData.event_type);
                            savePlannerSnapshotRaw(eventData, currentStep);
                            navigate(`/vendors/${v.vendor_id}?${params.toString()}`);
                          }}
                          className="flex-1 px-4 py-2.5 text-xs font-semibold text-emerald-600 hover:bg-emerald-50 transition-colors"
                          data-testid={`selected-vendor-view-${v.vendor_id}`}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {loadingVendors ? (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : !(/^\d{5}$/.test(eventData.location_zip.trim())) && !eventData.location_city.trim() && !eventData.location_state.trim() ? (
              <Card className="border-dashed border-gray-200">
                <CardContent className="py-10 text-center">
                  <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm mb-2">Add a location to see available vendors</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentStep(3)}
                    data-testid="step4-add-location-btn"
                  >
                    <MapPin className="w-3.5 h-3.5 mr-1.5" />
                    Set Location
                  </Button>
                </CardContent>
              </Card>
            ) : vendors.length === 0 ? (
              <Card className="border-dashed border-gray-200 bg-gray-50/50">
                <CardContent className="py-8 text-center space-y-3">
                  <Search className="w-6 h-6 text-emerald-400 mx-auto" />
                  <div>
                    <p className="text-gray-700 font-medium">No exact matches yet</p>
                    <p className="text-gray-400 text-sm mt-1">Try browsing providers{eventData.location_city ? ` in ${[eventData.location_city, eventData.location_state].filter(Boolean).join(', ')}` : ''}, or continue planning and invite vendors later.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center pt-1">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentStep(2)}
                      data-testid="step4-change-services-btn"
                    >
                      Change Services
                    </Button>
                    <Button 
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => {
                        setShowBrowseAll(true);
                        fetchBrowseAllVendors();
                      }}
                      data-testid="step4-browse-all-btn"
                    >
                      <Search className="w-3.5 h-3.5 mr-1.5" />
                      Browse providers{eventData.location_city ? ` in ${[eventData.location_city, eventData.location_state].filter(Boolean).join(', ')}` : ''}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {/* State-level discovery message */}
                {!(/^\d{5}$/.test(eventData.location_zip.trim())) && !eventData.location_city.trim() && eventData.location_state.trim() && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100" data-testid="state-fallback-message">
                    <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
                    <p className="text-xs text-amber-700">
                      Showing top vendors in {eventData.location_state} — add a ZIP code for more precise results
                    </p>
                  </div>
                )}
                {vendors.filter(v => !eventData.selected_vendors.some(sv => sv.vendor_id === v.id)).map(vendor => {
                  const isSelected = eventData.selected_vendors.some(v => v.vendor_id === vendor.id);
                  const priceLabel = formatPreviewPrice(vendor);
                  
                  return (
                    <div
                      key={vendor.id}
                      className={`rounded-xl border-2 text-left transition-all overflow-hidden ${
                        isSelected 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 bg-white hover:border-emerald-200'
                      }`}
                      data-testid={`vendor-${vendor.id}`}
                    >
                      <div className="flex items-center gap-4 p-4">
                        {/* Vendor photo — avatar with owner fallback */}
                        {(() => {
                          const avatar = getVendorAvatar(vendor);
                          const ci = getCategoryIcon(vendor);
                          const Icon = ci.icon;
                          return (
                            <div className={`w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden relative ${!avatar ? ci.bg : 'bg-gray-100'}`}>
                              {avatar && (
                                <img
                                  src={getImageUrl(avatar)}
                                  alt={vendor.business_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                                />
                              )}
                              <div className={`w-full h-full items-center justify-center ${ci.bg} ${ci.color} ${avatar ? 'hidden absolute inset-0' : 'flex'}`}>
                                <Icon className="w-6 h-6" />
                              </div>
                            </div>
                          );
                        })()}
                        
                        {/* Vendor info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{vendor.business_name}</p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {vendor.matched_category || vendor.categories?.[0]}
                            {vendor.city && <span className="text-gray-300 mx-1">&middot;</span>}
                            {vendor.city && <span>{vendor.city}</span>}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {(vendor.review_count || 0) > 0 ? (
                              <span className="flex items-center gap-0.5 text-xs text-gray-600">
                                <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                {(vendor.average_rating || 0).toFixed(1)}
                                <span className="text-gray-400">({vendor.review_count})</span>
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400" data-testid="no-reviews-label">No reviews yet</span>
                            )}
                            <span className={`text-xs font-medium ${priceLabel ? 'text-emerald-600' : 'text-gray-400'}`}>
                              {priceLabel || 'Request pricing'}
                            </span>
                          </div>
                          {(() => {
                            const avail = getVendorAvailability(vendor, eventData.event_date);
                            if (!avail) return null;
                            const styles = {
                              available: 'text-emerald-600 bg-emerald-50',
                              unavailable: 'text-amber-700 bg-amber-50',
                              unknown: 'text-gray-500 bg-gray-50'
                            };
                            const icons = {
                              available: <CheckCircle2 className="w-3 h-3" />,
                              unavailable: <AlertCircle className="w-3 h-3" />,
                              unknown: <Clock className="w-3 h-3" />
                            };
                            return (
                              <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[avail.status]}`} data-testid={`vendor-availability-${vendor.id}`}>
                                {icons[avail.status]} {avail.label}
                              </span>
                            );
                          })()}
                        </div>
                        
                        {/* Add/Remove button — 44px min touch target */}
                        <button
                          onClick={() => toggleVendor(vendor)}
                          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                          data-testid={`vendor-toggle-${vendor.id}`}
                          aria-label={isSelected ? 'Remove vendor' : 'Add vendor'}
                        >
                          {isSelected ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <Plus className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      
                      {/* View Profile — 44px min touch target */}
                      <button
                        onClick={() => setPreviewVendor(vendor)}
                        className="w-full px-4 py-3 border-t border-gray-100 text-xs text-emerald-600 font-medium text-center hover:bg-emerald-50/50 transition-colors active:bg-emerald-50"
                        data-testid={`vendor-profile-${vendor.id}`}
                      >
                        View Profile
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Additional providers auto-loaded below matched vendors */}
            
            {/* Browse all results — inline expansion */}
            {showBrowseAll && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-600">
                    {eventData.location_city || eventData.location_state
                      ? `More providers ${formatLocationHeader({ city: eventData.location_city, state: eventData.location_state }).toLowerCase()}`
                      : 'More providers'}
                  </p>
                </div>
                
                {loadingBrowseAll ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                    <span className="text-sm text-gray-400 ml-2">Loading providers...</span>
                  </div>
                ) : browseAllVendors.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-4">No additional providers found in this area.</p>
                ) : (
                  <div className="space-y-3">
                    {browseAllVendors.filter(v => !eventData.selected_vendors.some(sv => sv.vendor_id === v.id)).map(vendor => {
                      const isSelected = eventData.selected_vendors.some(v => v.vendor_id === vendor.id);
                      const priceInfo = getVendorPrice(vendor);
                      return (
                        <div key={`browse-${vendor.id}`} className={`border rounded-2xl overflow-hidden transition-all ${isSelected ? 'border-emerald-300 bg-emerald-50/40 ring-1 ring-emerald-200' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                          <div className="flex items-center gap-3 p-4">
                            {/* Vendor photo — avatar with owner fallback */}
                            {(() => {
                              const avatar = getVendorAvatar(vendor);
                              const ci = getCategoryIcon(vendor);
                              const Icon = ci.icon;
                              return (
                                <div className={`w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden relative ${!avatar ? ci.bg : 'bg-gray-100'}`}>
                                  {avatar && (
                                    <img
                                      src={getImageUrl(avatar)}
                                      alt={vendor.business_name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                                    />
                                  )}
                                  <div className={`w-full h-full items-center justify-center ${ci.bg} ${ci.color} ${avatar ? 'hidden absolute inset-0' : 'flex'}`}>
                                    <Icon className="w-6 h-6" />
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Vendor info */}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-base truncate">{vendor.business_name}</p>
                              <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">
                                {(vendor.categories || []).join(' · ')}
                                {vendor.city && <span className="text-gray-300 mx-1">&middot;</span>}
                                {vendor.city && <span>{vendor.city}</span>}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {vendor.average_rating > 0 && (
                                  <span className="flex items-center gap-0.5 text-xs text-gray-600">
                                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                    {vendor.average_rating.toFixed(1)}
                                  </span>
                                )}
                                {priceInfo.display && (
                                  <span className="text-xs text-emerald-600 font-medium">{priceInfo.display}</span>
                                )}
                              </div>
                            </div>

                            {/* Add/Remove toggle */}
                            <button
                              onClick={() => toggleVendor(vendor)}
                              className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                                isSelected ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-100 hover:bg-gray-200'
                              }`}
                              data-testid={`browse-vendor-select-${vendor.id}`}
                              aria-label={isSelected ? 'Remove vendor' : 'Add vendor'}
                            >
                              {isSelected ? (
                                <Check className="w-5 h-5 text-white" />
                              ) : (
                                <Plus className="w-5 h-5 text-gray-400" />
                              )}
                            </button>
                          </div>
                          <button
                            onClick={() => setPreviewVendor(vendor)}
                            className="w-full px-4 py-3 border-t border-gray-100 text-xs text-emerald-600 font-medium text-center hover:bg-emerald-50/50 transition-colors"
                            data-testid={`browse-vendor-profile-${vendor.id}`}
                          >
                            View Profile
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Step 5: Review & Send */}
        {currentStep === 5 && (
          <div className="space-y-4" data-testid="step-review">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Review Your Event Plan</h2>
              <p className="text-gray-500 mt-1">
                {eventData.selected_vendors.length > 0 
                  ? 'Ready to send to vendors' 
                  : 'Review your event details'}
              </p>
            </div>
            
            {/* Event Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium">{PRIMARY_EVENT_TYPES.find(t => t.value === eventData.event_type)?.label || eventData.event_type}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Date</span>
                  <span className="font-semibold text-gray-900">{new Date(eventData.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                {eventData.event_time && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Time</span>
                    <span className="font-medium">{eventData.event_time}</span>
                  </div>
                )}
                {eventData.guest_count && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Guests</span>
                    <span className="font-medium">{eventData.guest_count}</span>
                  </div>
                )}
                {(eventData.location_city || eventData.location_zip) && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Location</span>
                    <span className="font-medium">
                      {[eventData.location_city, eventData.location_state].filter(Boolean).join(', ') || eventData.location_zip}
                    </span>
                  </div>
                )}
                {eventData.budget && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Budget</span>
                    <span className="font-semibold text-emerald-600">{formatCurrency(parseFloat(eventData.budget))}</span>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Selected Vendors */}
            {eventData.selected_vendors.length > 0 ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    Selected Vendors ({eventData.selected_vendors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {eventData.selected_vendors.map(vendor => (
                    <div key={vendor.vendor_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium text-sm">{vendor.vendor_name}</p>
                        <p className="text-xs text-gray-500">{vendor.category}</p>
                      </div>
                      <span className={`text-sm font-medium ${vendor.starting_price_display || vendor.starting_price ? 'text-emerald-600' : 'text-gray-400 italic'}`}>
                        {vendor.starting_price_display || (vendor.starting_price ? formatCurrency(vendor.starting_price) : 'Quote on request')}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-dashed border-gray-200 bg-gray-50/50">
                <CardContent className="py-6 text-center">
                  <p className="text-sm text-gray-500">No vendors selected yet</p>
                  <p className="text-xs text-gray-400 mt-1">You can add vendors to this event later</p>
                </CardContent>
              </Card>
            )}
            
            {/* Optional Message — only when vendors are selected */}
            {eventData.selected_vendors.length > 0 && (
            <Card className="mb-32">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Message to Vendors (optional)</CardTitle>
                <CardDescription className="text-xs">Add any special requests or details</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Hi! I'm planning a special event and would love to work with you..."
                  value={eventData.customer_message}
                  onChange={(e) => setEventData(prev => ({ ...prev, customer_message: e.target.value }))}
                  className="min-h-[100px]"
                  data-testid="customer-message-input"
                />
              </CardContent>
            </Card>
            )}

            {/* Bottom spacer when no message card */}
            {eventData.selected_vendors.length === 0 && <div className="mb-32" />}
          </div>
        )}
      </div>
      
      {/* Sticky Bottom Bar - Hide when message input is focused */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 pb-safe">
        {/* Estimated Total (visible from step 5 onwards) */}
        {currentStep >= 4 && eventData.selected_vendors.length > 0 && (
          <div className="px-4 py-3 bg-emerald-50 border-b">
            <div className="max-w-2xl mx-auto flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Estimated total</p>
                <p className="text-xs text-gray-500">
                  {tbdCount > 0 && `${tbdCount} vendor${tbdCount !== 1 ? 's' : ''} awaiting quotes`}
                </p>
              </div>
              <div className="text-right">
                {estimatedTotal > 0 ? (
                  <>
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatCurrency(estimatedTotal)}
                    </p>
                    <p className="text-xs text-gray-400">Based on selected vendor pricing</p>
                  </>
                ) : (
                  <>
                    <p className="text-base font-semibold text-gray-600">Pricing after request</p>
                    <p className="text-xs text-gray-400">Vendors will send exact quotes</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Navigation Buttons */}
        <div className="px-4 py-3">
          <div className="max-w-2xl mx-auto flex gap-3">
            {currentStep > 1 && (
              <Button 
                variant="outline" 
                onClick={prevStep}
                className="flex-1"
              >
                Back
              </Button>
            )}
            
            {currentStep < 5 ? (
              <Button 
                onClick={nextStep}
                disabled={!canProceed()}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="next-step-btn"
              >
                Continue <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <Button 
                onClick={sendEventPlan}
                disabled={loading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="send-plan-btn"
              >
                {loading ? (
                  eventData.selected_vendors.length > 0 ? 'Sending...' : 'Creating...'
                ) : eventData.selected_vendors.length > 0 ? (
                  <>
                    <Send className="mr-2 w-4 h-4" /> Send My Event Plan
                  </>
                ) : (
                  <>
                    <Check className="mr-2 w-4 h-4" /> Create My Event
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Exit Event Planning?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved progress. Your selections will be lost if you exit now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Planning</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmExit}
              className="bg-gray-600 hover:bg-gray-700"
            >
              Exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Vendor Profile Preview Sheet */}
      {previewVendor && (() => {
        const catIcon = getCategoryIcon(previewVendor);
        const CatIconComp = catIcon.icon;
        const priceLabel = formatPreviewPrice(previewVendor);
        const insight = getFitInsight(previewVendor);
        const galleryPhotos = (previewVendor.portfolio_photos || []).filter(p => p && p.trim() && !p.includes('example.com') && !p.includes('placeholder'));
        const hasRating = previewVendor.average_rating > 0;
        const hasBookings = previewVendor.total_bookings > 0;

        return (
        <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="vendor-preview-modal">
          <div className="fixed inset-0 bg-black/40 transition-opacity" onClick={() => setPreviewVendor(null)} />
          <div className="relative bg-white w-full sm:max-w-lg sm:rounded-t-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto shadow-xl overscroll-contain animate-in slide-in-from-bottom duration-300">
            {/* Drag handle + close */}
            <div className="sticky top-0 z-10 bg-white rounded-t-2xl">
              <div className="flex justify-center pt-2.5 pb-1">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>
              <button
                onClick={() => setPreviewVendor(null)}
                className="absolute top-2.5 right-3 z-10 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                data-testid="vendor-preview-close"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="px-5 pt-1 pb-4 space-y-0">
              {/* ── HEADER SECTION ── */}
              <div className="flex items-start gap-3.5">
                {/* Avatar / owner fallback / category-icon fallback */}
                {(() => { const previewAvatar = getVendorAvatar(previewVendor); return (
                <div className={`w-16 h-16 rounded-xl flex-shrink-0 overflow-hidden ${!previewAvatar ? catIcon.bg : 'bg-gray-100'}`}>
                  {previewAvatar ? (
                    <img
                      src={getImageUrl(previewAvatar)}
                      alt={previewVendor.business_name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                    />
                  ) : null}
                  <div className={`w-full h-full items-center justify-center ${catIcon.color} ${previewAvatar ? 'hidden' : 'flex'}`}>
                    <CatIconComp className="w-7 h-7" />
                  </div>
                </div>
                ); })()}

                {/* 1. Name  2. Rating  */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 leading-tight truncate" data-testid="preview-vendor-name">
                    {previewVendor.business_name}
                  </h3>
                  {previewVendor.short_tagline && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{previewVendor.short_tagline}</p>
                  )}
                  {(hasRating || hasBookings) && (
                    <div className="flex items-center gap-2.5 mt-1.5 flex-wrap">
                      {hasRating && (
                        <span className="inline-flex items-center gap-1 text-sm" data-testid="preview-rating">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          <span className="font-semibold text-gray-900">{previewVendor.average_rating.toFixed(1)}</span>
                          {previewVendor.review_count > 0 && (
                            <span className="text-xs text-gray-400">({previewVendor.review_count} {previewVendor.review_count === 1 ? 'review' : 'reviews'})</span>
                          )}
                        </span>
                      )}
                      {hasBookings && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-testid="preview-bookings">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          {previewVendor.total_bookings} {previewVendor.total_bookings === 1 ? 'booking' : 'bookings'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Baseline info row — always renders for sparse vendors so the
                  sheet still aids triage when rating/bookings/etc. are absent.
                  Feb 2026: each chip renders independently; the row hides
                  entirely only when ALL three fields are missing. */}
              {(() => {
                const previewPrice = getVendorPrice(previewVendor);
                const previewCategory =
                  previewVendor.category ||
                  (Array.isArray(previewVendor.categories) ? previewVendor.categories[0] : null) ||
                  null;
                const previewDistance =
                  typeof previewVendor.distance_miles === 'number'
                    ? previewVendor.distance_miles
                    : null;
                const anyChip = previewCategory || previewPrice.display || previewDistance !== null;
                if (!anyChip) return null;
                return (
                  <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="preview-baseline">
                    {previewCategory && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-50 border border-gray-100 text-xs font-medium text-gray-700">
                        {previewCategory}
                      </span>
                    )}
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-xs font-semibold text-emerald-700">
                      {previewPrice.display || 'Request pricing'}
                    </span>
                    {previewDistance !== null && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-50 border border-gray-100 text-xs font-medium text-gray-600">
                        {previewDistance < 1 ? '< 1 mi' : `${Math.round(previewDistance)} mi`}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* 3. Badges */}
              {previewBadges.length > 0 && (
                <div className="mt-2.5" data-testid="preview-badges">
                  <BadgeList badges={previewBadges} size="sm" maxVisible={3} clickable={false} />
                </div>
              )}

              {/* 4-5. Response time + Social proof */}
              {(previewVendor.response_time_hours != null || previewVendor.bookings_this_month > 0) && (
                <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                  {previewVendor.response_time_hours != null && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-xs font-medium text-amber-700" data-testid="preview-response-time">
                      <Zap className="w-3 h-3 text-amber-500" />
                      Responds in {previewVendor.response_time_hours < 1 ? 'under 1 hr' : `~${Math.round(previewVendor.response_time_hours)} hr`}
                    </span>
                  )}
                  {previewVendor.bookings_this_month > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" data-testid="preview-social-proof">
                      <Users className="w-3 h-3" />
                      Booked {previewVendor.bookings_this_month} times this month
                    </span>
                  )}
                </div>
              )}

              {/* ── AVAILABILITY ── */}
              {(() => {
                const avail = getVendorAvailability(previewVendor, eventData.event_date);
                if (!avail) return null;
                const cfg = {
                  available: { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700', icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> },
                  unavailable: { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', icon: <AlertCircle className="w-3.5 h-3.5 text-amber-500" /> },
                  unknown: { bg: 'bg-gray-50', border: 'border-gray-100', text: 'text-gray-500', icon: <Clock className="w-3.5 h-3.5 text-gray-400" /> }
                };
                const s = cfg[avail.status];
                return (
                  <div className={`mt-3 px-3 py-2 rounded-lg ${s.bg} border ${s.border} flex items-center gap-2`} data-testid="preview-availability">
                    {s.icon}
                    <span className={`text-xs font-medium ${s.text}`}>{avail.label}</span>
                  </div>
                );
              })()}

              {/* ── DETAILS SECTION ── */}
              <div className="mt-3.5 pt-3.5 border-t border-gray-100 space-y-2.5">
                {/* Location + Radius */}
                {(previewVendor.city || previewVendor.state) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>{previewVendor.city}{previewVendor.state ? `, ${previewVendor.state}` : ''}</span>
                    {previewVendor.service_radius > 0 && !isFixedLocationProvider(previewVendor) && (
                      <>
                        <span className="text-gray-300">&middot;</span>
                        <span className="text-xs text-gray-400">{previewVendor.service_radius} mi radius</span>
                      </>
                    )}
                  </div>
                )}

                {/* Price */}
                {priceLabel && (
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600" data-testid="preview-price">
                    {priceLabel}
                  </div>
                )}

                {/* Fit Insight */}
                {insight && (
                  <p className="text-xs text-gray-400 italic pl-5.5" data-testid="preview-fit-insight">{insight}</p>
                )}
              </div>

              {/* ── SPECIALTIES ── */}
              {previewVendor.specialties?.length > 0 && (
                <div className="mt-3.5 pt-3 border-t border-gray-50" data-testid="preview-specialties">
                  <div className="flex flex-wrap gap-1.5">
                    {previewVendor.specialties.slice(0, 6).map((spec, i) => (
                      <span key={i} className="px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-full text-xs text-gray-600 font-medium">{spec}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── TOP OFFERINGS ── */}
              {previewVendor.services?.length > 0 && (() => {
                // Show most popular first, then first package, then first service
                const sorted = [...previewVendor.services].sort((a, b) => (b.is_most_popular ? 1 : 0) - (a.is_most_popular ? 1 : 0));
                const top = sorted.slice(0, 2);
                if (top.length === 0) return null;
                return (
                  <div className="mt-3.5 pt-3 border-t border-gray-50" data-testid="preview-offerings">
                    <p className="text-xs font-medium text-gray-500 mb-2">Top Offerings</p>
                    <div className="space-y-1.5">
                      {top.map((svc, i) => (
                        <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${svc.is_most_popular ? 'bg-emerald-50 border border-emerald-100' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {svc.is_most_popular && <Star className="w-3 h-3 text-emerald-600 fill-emerald-600 shrink-0" />}
                            <span className="text-xs font-semibold text-gray-800 truncate">{svc.name}</span>
                            {svc.type === 'package' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-600 font-medium shrink-0">PKG</span>}
                          </div>
                          {svc.starting_price && (
                            <span className="text-xs font-bold text-emerald-600 shrink-0 ml-2">
                              {typeof svc.starting_price === 'number' ? `$${svc.starting_price}` : svc.starting_price}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ── GALLERY ── */}
              {galleryPhotos.length > 0 && (
                <div className="mt-3.5 pt-3 border-t border-gray-50" data-testid="preview-gallery">
                  <p className="text-xs font-medium text-gray-500 mb-2">Gallery</p>
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                    {galleryPhotos.slice(0, 5).map((photo, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          const vendorId = previewVendor.id;
                          const params = new URLSearchParams();
                          params.set('fromPlan', 'true');
                          if (eventData.event_type) params.set('planEventType', eventData.event_type);
                          // Feb 2026: persist planner state + use React Router
                          // instead of window.open(_blank) which no-ops on iOS native.
                          savePlannerSnapshotRaw(eventData, currentStep);
                          setPreviewVendor(null);
                          navigate(`/vendors/${vendorId}?${params.toString()}`);
                        }}
                        className="w-[72px] h-[72px] rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-emerald-300 transition-all"
                      >
                        <img
                          src={getImageUrl(photo)}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── BIO ── */}
              {previewVendor.bio && (
                <div className="mt-3.5 pt-3 border-t border-gray-50">
                  <p className="text-xs font-medium text-gray-500 mb-1">About</p>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{previewVendor.bio}</p>
                </div>
              )}
            </div>

            {/* ── ACTION BUTTONS ── sticky bottom */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3.5 pb-[calc(0.875rem+env(safe-area-inset-bottom,0px))]">
              <div className="flex gap-3">
                {eventData.selected_vendors.some(v => v.vendor_id === previewVendor.id) ? (
                  <Button
                    onClick={() => { toggleVendor(previewVendor); setPreviewVendor(null); }}
                    variant="outline"
                    className="flex-1 h-11 rounded-xl border-red-200 text-red-600 hover:bg-red-50 font-semibold text-sm"
                    data-testid="vendor-preview-remove"
                  >
                    Remove from Plan
                  </Button>
                ) : (
                  <Button
                    onClick={() => { toggleVendor(previewVendor); setPreviewVendor(null); }}
                    className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold text-sm"
                    data-testid="vendor-preview-add"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    Add to Plan
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    const vendorId = previewVendor.id;
                    const params = new URLSearchParams();
                    params.set('fromPlan', 'true');
                    if (eventData.event_type) params.set('planEventType', eventData.event_type);
                    // Feb 2026: persist planner state + use React Router
                    // instead of window.open(_blank), which silently no-ops
                    // inside Capacitor iOS WKWebView.
                    savePlannerSnapshotRaw(eventData, currentStep);
                    setPreviewVendor(null);
                    navigate(`/vendors/${vendorId}?${params.toString()}`);
                  }}
                  className="h-11 rounded-xl px-4 font-semibold text-sm border-gray-200"
                  data-testid="vendor-preview-full-profile"
                >
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  View Details
                </Button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Auth Gate Modal */}
      <AuthGateModal open={showAuthGate} onClose={() => { setShowAuthGate(false); setCurrentStep(4); }} />
    </div>
  );
};

export default PlanEvent;
